/**
 * Generic spreadsheet time-entry import wizard.
 *
 * Three steps against /api/integrations/spreadsheet/*:
 *   1. Mapping — the analyze endpoint suggests column → field matches; the
 *      user adjusts them, optionally forces the day/month order for ambiguous
 *      dates, and can save/apply named mapping profiles (localStorage — an
 *      account-level store would need a new settings namespace).
 *   2. Preview — per-row validation errors, the date-format interpretation
 *      used for every row (never a silent guess), duplicate flags, and
 *      overlap warnings against existing entries. Import is blocked while
 *      any row is invalid because commit is all-or-nothing.
 *   3. Result — imported / skipped-duplicate counts.
 *
 * The file is re-posted at each step: uploads are small and keeping the
 * server stateless avoids staging tables.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const SPREADSHEET_PROFILES_STORAGE_KEY = 'kn.spreadsheetImportProfiles';

type MappingField =
  | 'start'
  | 'end'
  | 'duration'
  | 'project'
  | 'task'
  | 'description'
  | 'tags'
  | 'billable';

type MappingState = Record<MappingField, string>;

interface StoredProfile {
  mapping: Partial<MappingState>;
  date_order?: string;
}

interface AnalyzeResult {
  headers: string[];
  sample_rows: string[][];
  row_count: number;
  suggested_mapping: Partial<Record<MappingField, string>>;
}

interface PreviewRow {
  row: number;
  start: string | null;
  end: string | null;
  duration_seconds: number | null;
  project: string | null;
  task: string | null;
  description: string | null;
  tags: string | null;
  billable: boolean | null;
  date_format: string | null;
  duplicate: boolean;
  errors: string[];
  warnings: string[];
}

interface PreviewSummary {
  total_rows: number;
  valid: number;
  invalid: number;
  duplicates: number;
  overlap_warnings: number;
  to_import: number;
  total_duration_seconds: number;
  date_format: string;
}

interface PreviewResult {
  rows: PreviewRow[];
  summary: PreviewSummary;
}

interface CommitResult {
  provider: string;
  imported: number;
  skipped_duplicates: number;
  total_rows: number;
}

interface SpreadsheetImportDialogProps {
  open: boolean;
  file: File | null;
  onClose: () => void;
  onImported: (summary: string) => void;
}

const FIELD_DEFS: Array<{ key: MappingField; label: string }> = [
  { key: 'start', label: 'Start' },
  { key: 'end', label: 'End' },
  { key: 'duration', label: 'Duration' },
  { key: 'project', label: 'Project' },
  { key: 'task', label: 'Task' },
  { key: 'description', label: 'Description' },
  { key: 'tags', label: 'Tags' },
  { key: 'billable', label: 'Billable' },
];

const EMPTY_MAPPING: MappingState = {
  start: '',
  end: '',
  duration: '',
  project: '',
  task: '',
  description: '',
  tags: '',
  billable: '',
};

const loadProfiles = (): Record<string, StoredProfile> => {
  try {
    const raw = localStorage.getItem(SPREADSHEET_PROFILES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours === 0) return `${minutes}m`;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

const formatStamp = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const SpreadsheetImportDialog: React.FC<SpreadsheetImportDialogProps> = ({
  open,
  file,
  onClose,
  onImported,
}) => {
  const { dialogContentPaddingSx, tableCellPaddingSx } = useLayoutTokens();
  const [step, setStep] = useState<'mapping' | 'preview' | 'done'>('mapping');
  const [analyze, setAnalyze] = useState<AnalyzeResult | null>(null);
  const [mapping, setMapping] = useState<MappingState>(EMPTY_MAPPING);
  const [dateOrder, setDateOrder] = useState<'' | 'MDY' | 'DMY'>('');
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [result, setResult] = useState<CommitResult | null>(null);
  const [profiles, setProfiles] = useState<Record<string, StoredProfile>>({});
  const [selectedProfile, setSelectedProfile] = useState('');
  const [profileName, setProfileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !file) return;
    setStep('mapping');
    setAnalyze(null);
    setMapping(EMPTY_MAPPING);
    setDateOrder('');
    setPreview(null);
    setResult(null);
    setSelectedProfile('');
    setProfileName('');
    setError(null);
    setProfiles(loadProfiles());

    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post<AnalyzeResult>(
          `${API_BASE_URL}/api/integrations/spreadsheet/analyze`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        if (cancelled) return;
        setAnalyze(response.data);
        setMapping({ ...EMPTY_MAPPING, ...response.data.suggested_mapping });
      } catch (err: any) {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'Could not read the spreadsheet'));
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, file]);

  const mappingValid = Boolean(mapping.start && (mapping.end || mapping.duration));

  const buildMappingPayload = (): string => {
    const payload: Record<string, string> = {};
    FIELD_DEFS.forEach(({ key }) => {
      if (mapping[key]) payload[key] = mapping[key];
    });
    if (dateOrder) payload.date_order = dateOrder;
    return JSON.stringify(payload);
  };

  const postWithMapping = async <T,>(endpoint: string): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file as File);
    formData.append('mapping', buildMappingPayload());
    const response = await axios.post<T>(
      `${API_BASE_URL}/api/integrations/spreadsheet/${endpoint}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  };

  const handlePreview = async () => {
    try {
      setBusy(true);
      setError(null);
      setPreview(await postWithMapping<PreviewResult>('preview'));
      setStep('preview');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Preview failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    try {
      setBusy(true);
      setError(null);
      setResult(await postWithMapping<CommitResult>('commit'));
      setStep('done');
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveProfile = () => {
    const name = profileName.trim();
    if (!name) return;
    const stored: StoredProfile = { mapping: {} };
    FIELD_DEFS.forEach(({ key }) => {
      if (mapping[key]) stored.mapping[key] = mapping[key];
    });
    if (dateOrder) stored.date_order = dateOrder;
    const next = { ...profiles, [name]: stored };
    setProfiles(next);
    // Deliberately not pre-selecting the saved profile: MUI's Select only
    // fires onChange on a value *change*, so a pre-selected entry could not
    // be re-applied without picking another profile first.
    localStorage.setItem(SPREADSHEET_PROFILES_STORAGE_KEY, JSON.stringify(next));
  };

  const handleApplyProfile = (name: string) => {
    setSelectedProfile(name);
    const profile = profiles[name];
    if (!profile) return;
    const headers = analyze?.headers ?? [];
    const next = { ...EMPTY_MAPPING };
    FIELD_DEFS.forEach(({ key }) => {
      const header = profile.mapping[key];
      // Only apply columns this file actually has — a profile saved against
      // a different export shape should not produce unmappable columns.
      if (header && headers.includes(header)) next[key] = header;
    });
    setMapping(next);
    setDateOrder(profile.date_order === 'MDY' || profile.date_order === 'DMY'
      ? profile.date_order
      : '');
  };

  const handleFinish = () => {
    if (result) {
      onImported(
        `Spreadsheet import: imported ${result.imported}, ` +
          `skipped ${result.skipped_duplicates} duplicate(s).`,
      );
    } else {
      onClose();
    }
  };

  const summary = preview?.summary;

  const rowStatus = (row: PreviewRow): React.ReactNode => {
    if (row.errors.length > 0) {
      return (
        <Typography variant="caption" color="error">
          {row.errors.join('; ')}
        </Typography>
      );
    }
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        {row.duplicate && (
          <Chip size="small" label="Duplicate — will be skipped" color="default" />
        )}
        {row.warnings.map((warning) => (
          <Typography key={warning} variant="caption" color="warning.main">
            {warning}
          </Typography>
        ))}
        {!row.duplicate && row.warnings.length === 0 && (
          <Typography variant="caption" color="success.main">
            OK
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import spreadsheet{file ? ` — ${file.name}` : ''}</DialogTitle>
      <DialogContent sx={dialogContentPaddingSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 'mapping' && busy && !analyze && (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {step === 'mapping' && analyze && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Map the file&apos;s columns to time-entry fields. Start plus either
              End or Duration is required; whichever of the two is missing gets
              derived. Suggested matches are pre-selected.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="spreadsheet-profile-label">Saved profile</InputLabel>
                <Select
                  labelId="spreadsheet-profile-label"
                  label="Saved profile"
                  value={selectedProfile}
                  onChange={(e) => handleApplyProfile(e.target.value)}
                >
                  {Object.keys(profiles).length === 0 && (
                    <MenuItem value="" disabled>
                      No saved profiles yet
                    </MenuItem>
                  )}
                  {Object.keys(profiles).map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Profile name"
                value={profileName}
                onChange={(e) => setProfileName(e.target.value)}
              />
              <Button
                variant="outlined"
                onClick={handleSaveProfile}
                disabled={!profileName.trim()}
              >
                Save profile
              </Button>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {FIELD_DEFS.map(({ key, label }) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <FormControl fullWidth size="small">
                    <InputLabel id={`spreadsheet-map-${key}-label`}>{label}</InputLabel>
                    <Select
                      labelId={`spreadsheet-map-${key}-label`}
                      label={label}
                      value={mapping[key]}
                      onChange={(e) =>
                        setMapping((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    >
                      <MenuItem value="">
                        <em>Not mapped</em>
                      </MenuItem>
                      {analyze.headers.map((header) => (
                        <MenuItem key={header} value={header}>
                          {header}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              ))}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="spreadsheet-date-order-label">Date order</InputLabel>
                  <Select
                    labelId="spreadsheet-date-order-label"
                    label="Date order"
                    value={dateOrder}
                    onChange={(e) => setDateOrder(e.target.value as '' | 'MDY' | 'DMY')}
                  >
                    <MenuItem value="">Auto-detect</MenuItem>
                    <MenuItem value="MDY">MM/DD/YYYY</MenuItem>
                    <MenuItem value="DMY">DD/MM/YYYY</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {!mappingValid && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Map a Start column plus either an End or a Duration column to
                continue.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              First rows of the file ({analyze.row_count} data row
              {analyze.row_count === 1 ? '' : 's'} total)
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}
              >
                <TableHead>
                  <TableRow>
                    {analyze.headers.map((header) => (
                      <TableCell key={header}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyze.sample_rows.map((row, rowIdx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <TableRow key={rowIdx}>
                      {analyze.headers.map((header, cellIdx) => (
                        <TableCell key={header}>{row[cellIdx] ?? ''}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {step === 'preview' && preview && summary && (
          <>
            <Alert
              severity={summary.invalid > 0 ? 'error' : 'success'}
              sx={{ mb: 1 }}
            >
              {summary.invalid > 0
                ? `${summary.invalid} of ${summary.total_rows} row(s) have errors — ` +
                  'fix the file or the mapping; the import is all-or-nothing.'
                : `${summary.to_import} of ${summary.total_rows} row(s) will be ` +
                  `imported (${formatDuration(summary.total_duration_seconds)} total)` +
                  (summary.duplicates > 0
                    ? `; ${summary.duplicates} duplicate(s) will be skipped.`
                    : '.')}
            </Alert>
            {summary.overlap_warnings > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {summary.overlap_warnings} row(s) overlap existing time entries —
                review them below before importing.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Dates read as: <strong>{summary.date_format}</strong>
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                size="small"
                sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Date format</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row) => (
                    <TableRow key={row.row}>
                      <TableCell>{row.row}</TableCell>
                      <TableCell>{formatStamp(row.start)}</TableCell>
                      <TableCell>{formatStamp(row.end)}</TableCell>
                      <TableCell>
                        {row.duration_seconds != null
                          ? formatDuration(row.duration_seconds)
                          : '—'}
                      </TableCell>
                      <TableCell>{row.project || '—'}</TableCell>
                      <TableCell>{row.description || '—'}</TableCell>
                      <TableCell>{row.tags || '—'}</TableCell>
                      <TableCell>{row.date_format || '—'}</TableCell>
                      <TableCell>{rowStatus(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}

        {step === 'done' && result && (
          <Alert severity="success">
            Imported {result.imported} time entr
            {result.imported === 1 ? 'y' : 'ies'};{' '}
            {result.skipped_duplicates} duplicate(s) skipped.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'mapping' && (
          <>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={busy || !analyze || !mappingValid}
            >
              {busy && analyze ? <CircularProgress size={20} /> : 'Preview'}
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button onClick={() => setStep('mapping')} disabled={busy}>
              Back
            </Button>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCommit}
              disabled={
                busy ||
                !summary ||
                summary.invalid > 0 ||
                summary.to_import === 0
              }
            >
              {busy ? (
                <CircularProgress size={20} />
              ) : (
                `Import ${summary?.to_import ?? 0} entries`
              )}
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button variant="contained" onClick={handleFinish}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SpreadsheetImportDialog;
